import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Badge } from "@client/components/Badge";
import { EmptyState } from "@client/components/EmptyState";
import { TextField } from "@client/components/Field";
import { ErrorNotice, LoadingBlock } from "@client/components/Notices";
import { Panel } from "@client/components/Panel";
import { useAdminUsers } from "@client/hooks/useAdmin";
import { useDebouncedValue } from "@client/hooks/useDebouncedValue";
import { formatDate, formatRelative } from "@client/lib/datetime";
import { ROLE_LABEL } from "@client/lib/roles";

/**
 * `/admin/users` — everyone who has ever arrived.
 *
 * Distinct from the allow list on purpose. That list is who *may* sign in; this
 * is who has, what they have built, and whether their access is still live —
 * which is the view you want before revoking somebody, because "they have 14
 * scenarios" is the part that decides whether anyone else needs telling.
 */
export function AdminUsersPage() {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const filters = { q: debouncedSearch.trim() || undefined };
  const users = useAdminUsers(filters);

  const items = users.data?.items ?? [];

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-bold text-ink">Users</h1>
        <p className="max-w-prose text-sm text-ink-soft">
          A row appears here the first time someone signs in, or as soon as they are given a
          scenario or a product membership by email. Revoking access never removes the row or their
          work.
        </p>
      </header>

      <Panel>
        <div className="max-w-sm">
          <TextField
            label="Search"
            type="search"
            placeholder="Name or email"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        {users.isPending && <LoadingBlock rows={4} label="Loading users" />}

        {users.isError && <ErrorNotice error={users.error} onRetry={() => void users.refetch()} />}

        {users.isSuccess && items.length === 0 && (
          <EmptyState
            title={filters.q ? "No users match that search" : "Nobody has signed in yet"}
            description={
              filters.q ? (
                "Nothing here answers to that name or address."
              ) : (
                <>
                  People appear here once they sign in. Until then they are only entries on the{" "}
                  <Link to="/admin/allowlist" className="font-semibold text-ink underline">
                    allow list
                  </Link>
                  .
                </>
              )
            }
          />
        )}

        {items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[44rem] border-collapse text-sm">
              <thead>
                <tr className="border-b border-line text-left text-[11px] uppercase tracking-wider text-ink-soft">
                  <th scope="col" className="px-3 py-2 font-semibold">
                    Person
                  </th>
                  <th scope="col" className="px-3 py-2 font-semibold">
                    Role
                  </th>
                  <th scope="col" className="px-3 py-2 font-semibold">
                    Last sign-in
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-semibold">
                    Scenarios
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-semibold">
                    Products owned
                  </th>
                  <th scope="col" className="px-3 py-2 font-semibold">
                    Access
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((user) => (
                  <tr key={user.id} className="border-b border-line align-top">
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-semibold text-ink">{user.fullName}</span>
                        <span className="text-[11px] text-ink-soft">{user.email}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-soft">{ROLE_LABEL[user.role]}</td>
                    <td className="px-3 py-2 text-xs text-ink-soft">
                      <div className="flex flex-col gap-0.5">
                        <span>{user.lastLoginAt ? formatRelative(user.lastLoginAt) : "Never"}</span>
                        <span>Added {formatDate(user.createdAt)}</span>
                      </div>
                    </td>
                    <td className="tabular px-3 py-2 text-right text-ink">{user.scenarioCount}</td>
                    <td className="tabular px-3 py-2 text-right text-ink">{user.productCount}</td>
                    <td className="px-3 py-2">
                      {user.allowListed ? (
                        <Badge kind="positive">Allowed</Badge>
                      ) : (
                        <Badge kind="critical" title="They cannot sign in; their work is kept.">
                          Revoked
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {users.data && users.data.total > items.length && (
          <p className="text-xs text-ink-soft">
            Showing {items.length} of {users.data.total}. Narrow the search to see the rest.
          </p>
        )}
      </Panel>
    </div>
  );
}
